/**
 * Access-token persistence. Staff (ops console) and tenant users (dashboard)
 * each keep their own token under their own `localStorage` key, so signing in
 * to one never signs you out of, or into, the other. The fetch client
 * (`apiClient.ts`) reads the matching store to attach `Authorization: Bearer`.
 */
export interface TokenStore {
  /** The stored access token, or `null` when signed out. */
  get(): string | null
  /** Persist the access token after a successful login. */
  set(token: string): void
  /** Remove the access token on logout or a 401. */
  clear(): void
}

const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/

function createTokenStorage(key: string): TokenStore {
  return {
    get(): string | null {
      const token = localStorage.getItem(key)
      if (token == null) {
        return null
      }
      if (!JWT_SHAPE.test(token)) {
        localStorage.removeItem(key)
        return null
      }
      return token
    },
    set(token: string): void {
      localStorage.setItem(key, token)
    },
    clear(): void {
      localStorage.removeItem(key)
    },
  }
}

/** Staff ops console session. The key predates tenants; keeping it keeps staff signed in. */
export const staffTokenStorage = createTokenStorage('app.accessToken')

/** Tenant dashboard session. */
export const tenantTokenStorage = createTokenStorage('pixelsign.tenantToken')
